import React, { useEffect, useState } from "react";
import { Factory } from "lucide-react";
import { toast } from "react-hot-toast";

import MEFuelInUse from "../../components/Fleet/FleetPerformance/MEFuelInUse";
import NavigationData from "../../components/Fleet/FleetPerformance/NavigationData";
import AEData from "../../components/Fleet/FleetPerformance/AEData";
import RemainingFuelOnboard from "../../components/Fleet/FleetPerformance/RemainingFuelOnboard";
import EnginePowerGraph from "../../components/Fleet/FleetPerformance/EnginePowerGraph";
import EngineRPMGraph from "../../components/Fleet/FleetPerformance/EngineRPMGraph";
import EngineSFOCGraph from "../../components/Fleet/FleetPerformance/EngineSFOCGraph";
import FuelConsumptionChart from "../../components/Fleet/FleetPerformance/FuelConsumptionChart";
import DateRangePicker from "../../components/Common/DateRangePicker";
import PropellerSlipGraph from "../../components/Fleet/FleetPerformance/PropellerSlipGraph";

import FleetHeader from "../../components/Fleet/FleetHeader";
import { fetchFleets } from "../../services/fleetService";
import * as fleetPerformanceService from "../../services/fleetPerformanceService";
import CollapsibleSection from "../../components/Common/CollapsibleSection";
import { useSelector } from "react-redux";

const FleetPerformance = () => {
  const [currentFleet, setCurrentFleet] = useState("Fleet Alpha");
  const [loading, setLoading] = useState(false);
  const [fleets, setFleets] = useState([]);
  const { startDate, endDate } = useSelector((state) => state.dateRange);
  const [performanceMetadata, setPerformanceMetadata] = useState({
    meFuelData: {
      meFuelCons: "0.00",
      fuelPower: "0.00",
      totalPower: "0.00",
      fuelId: "XYZ123",
    },
    aeData: {
      aeFuelCons: "0.00",
      totalPower: "0.00",
    },
    navigationData: {
      gpsSpeed: "0.00",
      logSpeed: "0.00",
      rudderAngle: "0.00",
      heading: "0.00",
    },
  });

  const [fuelConsumptionData, setFuelConsumptionData] = useState([]);
  const [remainingFuelData, setRemainingFuelData] = useState([]);
  const [mainEnginePowerData, setMainEnginePowerData] = useState([]);
  const [mainEngineSFOCData, setMainEngineSFOCData] = useState([]);

  useEffect(() => {
    const fetchFleetsData = async () => {
      setLoading(true);
      try {
        const res = await fetchFleets();
        if (res.data.status === "success") {
          setFleets(res.data.data);
          if (res.data.data.length > 0) {
            setCurrentFleet(res.data.data[0]);
          }
        } else {
          toast.error("Failed to load fleets");
        }
      } catch (err) {
        toast.error("Error loading fleets");
      } finally {
        setLoading(false);
      }
    };
    fetchFleetsData();
  }, []);

  useEffect(() => {
    const fetchMetadata = async () => {
      const fleetId = currentFleet?.fleetId;
      if (fleetId) {
        setLoading(true);
        const startTime = Date.now();

        const metadataRes =
          await fleetPerformanceService.fetchFleetPerformanceMetadata(fleetId, {
            startDate,
            endDate,
          });
        setPerformanceMetadata(metadataRes.data.data);

        const fuelConsumptionRes =
          await fleetPerformanceService.fetchFleetFuelConsumption(fleetId, {
            startDate,
            endDate,
          });
        setFuelConsumptionData(fuelConsumptionRes.data.data);

        const remainingFuelRes =
          await fleetPerformanceService.fetchFleetRemainingFuel(fleetId, {
            startDate,
            endDate,
          });
        setRemainingFuelData(remainingFuelRes.data.data);

        const mainEnginePowerRes =
          await fleetPerformanceService.fetchFleetMainEnginePower(fleetId, {
            startDate,
            endDate,
          });
        setMainEnginePowerData(mainEnginePowerRes.data.data);

        const mainEngineSFOCRes =
          await fleetPerformanceService.fetchFleetSFOCPower(fleetId, {
            startDate,
            endDate,
          });
        setMainEngineSFOCData(mainEngineSFOCRes.data.data);

        const elapsed = Date.now() - startTime;
        const remainingDelay = Math.max(0, 2000 - elapsed);

        setTimeout(() => {
          setLoading(false);
        }, remainingDelay);
      }
    };

    fetchMetadata();
  }, [currentFleet, startDate, endDate]);

  return (
    <div className="relative">
      <div className="flex flex-col space-y-4 md:flex-row md:items-center md:justify-between mb-6">
        <FleetHeader
          currentFleet={currentFleet}
          fleets={fleets}
          onFleetChange={setCurrentFleet}
          pageTitle="Emission Analytics"
        />
        <div className="flex">
          <DateRangePicker />
        </div>
      </div>

      <div className="">
        <div className="single-row-below-1024 flex gap-4 mb-6">
          <div className="w-1/2 full-width-below-1024">
            <CollapsibleSection
              title="ME Fuel Consumption"
              icon={Factory}
              defaultCollapsed={false}
            >
              <div className="h-[500px] flex items-center justify-center">
                <FuelConsumptionChart
                  data={fuelConsumptionData}
                  isLoading={loading}
                />
              </div>
            </CollapsibleSection>
          </div>

          <div className="w-1/2 full-width-below-1024">
            <CollapsibleSection
              title="Remaining Fuel Onboard"
              icon={Factory}
              defaultCollapsed={false}
            >
              <div className="h-[500px] flex items-center justify-center">
                <RemainingFuelOnboard
                  data={remainingFuelData}
                  isLoading={loading}
                />
              </div>
            </CollapsibleSection>
          </div>
        </div>

        {/* Bottom Rows */}
        <div className="single-row-below-1024 flex gap-4 mb-6">
          <div className="w-1/2 full-width-below-1024">
            <CollapsibleSection
              title="Main Engine Power"
              icon={Factory}
              defaultCollapsed={false}
            >
              <div className="h-[500px] flex items-center justify-center">
                <EnginePowerGraph
                  data={mainEnginePowerData}
                  isLoading={loading}
                />
              </div>
            </CollapsibleSection>
          </div>
          <div className="w-1/2 full-width-below-1024">
            <CollapsibleSection
              title="SFOC Efficiency"
              icon={Factory}
              defaultCollapsed={false}
            >
              <div className="h-[500px] flex items-center justify-center">
                <EngineSFOCGraph
                  data={mainEngineSFOCData}
                  isLoading={loading}
                />
              </div>
            </CollapsibleSection>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FleetPerformance;
